import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useApp } from "../state/app";
import ReportSheet from "../components/ReportSheet";
import { ScadaV2DataTab } from "./ScadaV2Panel";
import { AutoDataTab } from "./Automatika";
import type { AutoCatalog } from "../types";
import { apiWindow, fmtD, fmtDT, fmtT } from "../utils/period";

interface ReportRow { id: number; unit: number; from_ts: string; to_ts: string; step_s: number; filename: string; pages: number; bytes: number; created_at: string; sent_at: string | null; url: string }

// «Отчёты»: build a PDF for the period selected in «Телеметрия», the list of
// everything already generated (open / send to the chat), and the Excel
// exports for admins.
export default function ReportsPanel({ isAdmin, hasUnits, hasPa }: { isAdmin: boolean; hasUnits: boolean; hasPa: boolean }) {
  const { t } = useT();
  const app = useApp();
  const [rows, setRows] = useState<ReportRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);
  const [catalog, setCatalog] = useState<AutoCatalog | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const unit = app.unit;

  const load = () => api.get<ReportRow[]>("/api/scada_v2/reports?limit=50").then(setRows).catch((e: ApiError) => setErr(e.message));
  useEffect(() => { if (hasUnits) load(); }, [hasUnits]);
  useEffect(() => { if (hasUnits) api.get<AutoCatalog>(`/api/scada_v2/metrics?unit=${unit}`).then(setCatalog).catch(() => undefined); }, [unit, hasUnits]);

  const open = (r: ReportRow) => {
    const url = `${window.location.origin}${r.url}`;
    const w = tg();
    if (w?.openLink) w.openLink(url); else window.open(url, "_blank");
  };
  const send = async (r: ReportRow) => {
    setSending(r.id); setErr(null);
    try { await api.post(`/api/scada_v2/report/${r.id}/send`); tg()?.HapticFeedback?.notificationOccurred("success"); load(); }
    catch (e) { setErr((e as ApiError).message); }
    finally { setSending(null); }
  };
  const sel = app.sel;
  const selLine = sel.from && sel.to ? (sel.from.slice(0, 10) === sel.to.slice(0, 10) ? `${fmtD(sel.from)} ${fmtT(sel.from)} → ${fmtT(sel.to)}` : `${fmtDT(sel.from)} → ${fmtDT(sel.to)}`) : t("period.q_all");
  const win = apiWindow(sel);
  const qs = (() => { const p = new URLSearchParams({ unit: String(unit) }); if (win.from) p.set("from", win.from); if (win.to) p.set("to", win.to); return p.toString(); })();
  const paWin = apiWindow(app.paSel);
  const pa = app.paSel;
  const paLine = pa.from && pa.to ? (pa.from.slice(0, 10) === pa.to.slice(0, 10) ? `${fmtD(pa.from)} ${fmtT(pa.from)} → ${fmtT(pa.to)}` : `${fmtDT(pa.from)} → ${fmtDT(pa.to)}`) : t("period.q_all");
  const paQs = (() => { const p = new URLSearchParams({ unit: "1" }); if (paWin.from) p.set("from", paWin.from); if (paWin.to) p.set("to", paWin.to); return p.toString(); })();
  const mb = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

  return (
    <div>
      <h1>{t("tabs.reports")}</h1>
      {err && <div className="alert error">{err}</div>}
      {hasUnits && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("reports.current")}</div>
          <div style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: "tabular-nums", margin: "2px 0 8px" }}>G{unit} · {selLine}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn pdf" onClick={() => setSheet(true)}>📄 {t("reports.new_pdf")}</button>
            <button className="btn secondary" style={{ maxWidth: 150 }} onClick={() => app.setTab("telemetry")}>{t("period.change")}</button>
          </div>
        </div>
      )}
      {sheet && hasUnits && <ReportSheet unit={unit} sel={sel} catalog={catalog} onClose={() => { setSheet(false); load(); }} />}

      {hasUnits && (
        <div className="card" style={{ marginBottom: 12 }}>
          <b style={{ fontSize: 14 }}>{t("reports.generated")}</b>
          {!rows && <div className="empty-state"><span className="spinner" /></div>}
          {rows && rows.length === 0 && <div style={{ fontSize: 13, color: "var(--hint)", marginTop: 6 }}>{t("reports.none")}</div>}
          {rows?.map(r => (
            <div key={r.id} style={{ padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>G{r.unit} · {r.from_ts.slice(0, 10) === r.to_ts.slice(0, 10) ? `${fmtD(r.from_ts)} ${fmtT(r.from_ts)} → ${fmtT(r.to_ts)}` : `${fmtDT(r.from_ts)} → ${fmtDT(r.to_ts)}`}</span>
                <span style={{ color: "var(--hint)", fontSize: 11, whiteSpace: "nowrap" }}>{r.pages} {t("report.pages")} · {mb(r.bytes)}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--hint)" }}>{t("reports.made")} {fmtDT(r.created_at)}{r.sent_at ? ` · ${t("reports.sent")}` : ""}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button className="btn secondary" style={{ padding: "7px 10px", fontSize: 13, minHeight: 34 }} onClick={() => open(r)}>{t("report.open")}</button>
                <button className="btn secondary" style={{ padding: "7px 10px", fontSize: 13, minHeight: 34 }} disabled={sending === r.id} onClick={() => send(r)}>
                  {sending === r.id ? <span className="spinner" /> : t("report.send_now")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdmin && (hasUnits || hasPa) && (
        <div className="card">
          <b style={{ fontSize: 14 }}>{t("reports.excel")}</b>
          {hasUnits && <div style={{ marginTop: 8 }}><div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>G{unit} · {selLine}</div><ScadaV2DataTab unit={unit} qs={qs} onErr={setErr} mode="excel" /></div>}
          {hasPa && <div style={{ marginTop: 8 }}><div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("telemetry.src_pa")} · {paLine}</div><AutoDataTab unit={1} qs={paQs} onErr={setErr} mode="excel" /></div>}
        </div>
      )}
    </div>
  );
}
